import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

/**
 * OdorikSipPhone — WebRTC softphone volající přímo z prohlížeče přes Odorik.
 * Používá JsSIP + SIP-over-WebSocket bránu Odoriku (wss://wss.odorik.cz).
 * Zobrazí se jen když je aktivní poskytovatel Odorik v režimu 'webrtc' a uživatel má SIP linku.
 * Reaguje na globální event z karty zákazníka: window 'twilio:call' { number, companyId, companyName, contactName }
 */
export default function OdorikSipPhone({ userId, isDarkMode }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);      // má uživatel WebRTC softphone k dispozici
  const [regState, setRegState] = useState('idle'); // idle | connecting | registered | failed
  const [regError, setRegError] = useState('');
  const [callState, setCallState] = useState('idle'); // idle | connecting | active | incoming
  const [callInfo, setCallInfo] = useState(null);   // { number, companyId, companyName, contactName }
  const [dialNumber, setDialNumber] = useState('');
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [activeNumber, setActiveNumber] = useState(''); // číslo (CLI), pod kterým uživatel volá
  const [lines, setLines] = useState([]);               // dostupné SIP linky uživatele
  const [activeLineIdx, setActiveLineIdx] = useState(0); // index aktivní linky
  const [expanded, setExpanded] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [logFilter, setLogFilter] = useState('all'); // all | missed
  const [missedSeenAt, setMissedSeenAt] = useState(0);

  // Zmeškaný hovor = příchozí, který nebyl přijat (nezvednutý/odmítnutý/nedostupný)
  const isMissedCall = (c) => c.direction === 'inbound' && ['no-answer', 'busy', 'failed', 'canceled'].includes(c.status);
  const missedCalls = recentCalls.filter(isMissedCall);
  const unseenMissedCount = missedCalls.filter(c => new Date(c.startedAt).getTime() > missedSeenAt).length;

  const markMissedSeen = useCallback(() => {
    const now = Date.now();
    setMissedSeenAt(now);
    if (userId) localStorage.setItem(`odorik_missed_seen_${userId}`, String(now));
  }, [userId]);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const credsRef = useRef(null); // { wssUrl, domain, line, password, callerId }
  // Nahrávání hovoru (mix mikrofon + protistrana), ukládá se na server jako u Twilia
  const recEnabledRef = useRef(false);
  const recRef = useRef(null);        // MediaRecorder
  const recStartingRef = useRef(false); // synchronní zámek proti dvojímu startu (accepted + confirmed)
  const recChunksRef = useRef([]);
  const recCtxRef = useRef(null);     // AudioContext
  const recStartRef = useRef(0);
  const recMetaRef = useRef(null);    // { number, companyId, companyName, direction }

  // Číslo do formátu vhodného pro Odorik SIP (00420…)
  const normalize = (n) => {
    let c = String(n || '').replace(/[^\d+]/g, '');
    if (!c) return '';
    if (c.startsWith('+')) c = '00' + c.slice(1);
    else if (c.startsWith('00')) { /* ok */ }
    else if (c.length === 9) c = '00420' + c;
    else if (c.startsWith('420')) c = '00' + c;
    else c = '00' + c;
    return c;
  };

  // ---- Načti čas posledního zobrazení zmeškaných hovorů (per uživatel) ----
  useEffect(() => {
    if (!userId) return;
    const stored = parseInt(localStorage.getItem(`odorik_missed_seen_${userId}`) || '0', 10);
    setMissedSeenAt(stored || 0);
  }, [userId]);

  // ---- Zjisti, zda má uživatel WebRTC softphone aktivní ----
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/twilio/user-config?userId=${userId}`);
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        setActive(!!(d.enabled && d.provider === 'odorik' && d.odorikMode === 'webrtc'));
        recEnabledRef.current = !!d.recordingEnabled;
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const fetchRecentCalls = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/twilio/calls?userId=${userId}&agentUserId=${userId}&limit=20`);
      if (res.ok) { const d = await res.json(); setRecentCalls(d.calls || []); }
    } catch { /* ignore */ }
  }, [userId]);

  useEffect(() => { if (active) fetchRecentCalls(); }, [active, fetchRecentCalls]);

  // Pravidelně kontroluj nové hovory (zachytí zmeškané hovory i když je panel zavřený)
  useEffect(() => {
    if (!active) return;
    const id = setInterval(fetchRecentCalls, 20000);
    return () => clearInterval(id);
  }, [active, fetchRecentCalls]);

  const cleanupCall = useCallback(() => {
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch { /* */ }
    sessionRef.current = null;
    setCallState('idle');
    setMuted(false);
    setElapsed(0);
    setCallInfo(null);
    fetchRecentCalls();
  }, [fetchRecentCalls]);

  // Odešle nahrávku na server (uloží se do Cloudu + spáruje s hovorem/firmou jako u Twilia)
  const uploadRecording = useCallback(() => {
    const chunks = recChunksRef.current;
    recChunksRef.current = [];
    try { recCtxRef.current && recCtxRef.current.close(); } catch { /* */ }
    recCtxRef.current = null;
    recRef.current = null;
    const meta = recMetaRef.current || {};
    if (!chunks.length) return;
    const blob = new Blob(chunks, { type: 'audio/webm' });
    const durationSec = Math.max(1, Math.round((Date.now() - (recStartRef.current || Date.now())) / 1000));
    const fd = new FormData();
    fd.append('file', blob, 'hovor.webm');
    fd.append('userId', userId);
    fd.append('companyId', meta.companyId || '');
    fd.append('companyName', meta.companyName || '');
    fd.append('toNumber', meta.direction === 'inbound' ? '' : (meta.number || ''));
    fd.append('fromNumber', meta.direction === 'inbound' ? (meta.number || '') : '');
    fd.append('direction', meta.direction || 'outbound');
    fd.append('durationSec', String(durationSec));
    fetch(`${API_URL}/twilio/odorik/recording`, { method: 'POST', body: fd }).catch(() => {});
  }, [userId]);

  // Spustí nahrávání – smíchá mikrofon (senders) a protistranu (receivers) do jedné stopy
  const startRecording = useCallback(async (session) => {
    if (!recEnabledRef.current || recRef.current || recStartingRef.current) return;
    recStartingRef.current = true; // zamkni synchronně PŘED await, ať 'confirmed' nespustí druhý rekordér
    try {
      const pc = session && session.connection;
      if (!pc || !window.MediaRecorder) return;
      const send = pc.getSenders().map(s => s.track).filter(t => t && t.kind === 'audio');
      const recv = pc.getReceivers().map(r => r.track).filter(t => t && t.kind === 'audio');
      if (!send.length && !recv.length) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      // AudioContext vytvořený v callbacku startuje 'suspended' → bez resume() by se nahrálo ticho
      if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* */ } }
      const dest = ctx.createMediaStreamDestination();
      const added = new Set();
      const addTrack = (track) => {
        if (!track || track.kind !== 'audio' || added.has(track.id)) return;
        added.add(track.id);
        try { ctx.createMediaStreamSource(new MediaStream([track])).connect(dest); } catch { /* */ }
      };
      send.forEach(addTrack);
      recv.forEach(addTrack);
      // pozdě příchozí vzdálená stopa (ICE ještě nebyla hotová při startu)
      try { pc.addEventListener('track', (ev) => addTrack(ev.track)); } catch { /* */ }
      recCtxRef.current = ctx;
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(dest.stream, mime ? { mimeType: mime } : undefined);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) recChunksRef.current.push(e.data); };
      rec.onstop = () => uploadRecording();
      rec.start(1000);
      recRef.current = rec;
      recStartRef.current = Date.now();
    } catch { /* */ } finally {
      recStartingRef.current = false;
    }
  }, [uploadRecording]);

  // Napojí příchozí zvuk z protistrany na <audio> – robustně přes getReceivers()
  const attachRemoteAudio = useCallback((session) => {
    const pc = session && session.connection;
    if (!pc) return;
    const pull = () => {
      try {
        const remote = new MediaStream();
        pc.getReceivers().forEach(r => { if (r.track && r.track.kind === 'audio') remote.addTrack(r.track); });
        if (remote.getTracks().length && audioRef.current) {
          audioRef.current.srcObject = remote;
          audioRef.current.play().catch(() => {});
        }
      } catch { /* */ }
    };
    try { pc.addEventListener('track', pull); } catch { /* */ }
    pull();
  }, []);

  const wireSession = useCallback((session) => {
    sessionRef.current = session;
    session.on('peerconnection', () => attachRemoteAudio(session));
    if (session.connection) attachRemoteAudio(session);
    // 'progress' (vytáčím) jen pro ODCHOZÍ hovory – ať nepřepíše stav „příchozí"
    if (session.direction === 'outgoing') {
      session.on('progress', () => setCallState('connecting'));
    }
    session.on('accepted', () => { setCallState('active'); attachRemoteAudio(session); startRecording(session); });
    session.on('confirmed', () => { setCallState('active'); attachRemoteAudio(session); startRecording(session); });
    session.on('ended', () => cleanupCall());
    session.on('failed', () => cleanupCall());
  }, [attachRemoteAudio, cleanupCall, startRecording]);

  // ---- Načtení SIP linek uživatele ----
  useEffect(() => {
    if (!active || !userId) { setLines([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/twilio/odorik/sip-credentials?userId=${userId}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setRegError(err.error || `SIP údaje se nepodařilo získat (HTTP ${res.status})`);
          setRegState('failed');
          return;
        }
        const d = await res.json();
        if (cancelled) return;
        credsRef.current = { wssUrl: d.wssUrl, domain: d.domain };
        const ls = Array.isArray(d.lines) ? d.lines : [];
        const savedLine = localStorage.getItem('odorikActiveLine');
        const found = ls.findIndex(l => l.line === savedLine);
        setActiveLineIdx(found >= 0 ? found : 0);
        setLines(ls);
      } catch {
        if (!cancelled) { setRegError('SIP údaje se nepodařilo získat'); setRegState('failed'); }
      }
    })();
    return () => { cancelled = true; };
  }, [active, userId]);

  // ---- Inicializace JsSIP UA pro aktivní linku (přeregistruje se při přepnutí linky) ----
  useEffect(() => {
    const meta = credsRef.current;
    const activeLine = lines[activeLineIdx];
    if (!active || !userId || !meta || !activeLine) return;
    let cancelled = false;
    let ua = null;
    setActiveNumber(activeLine.callerId || activeLine.line || '');

    (async () => {
      let JsSIP;
      try {
        const mod = await import('jssip');
        JsSIP = mod.default || mod;
        if (!JsSIP || !JsSIP.UA) throw new Error('Modul jssip neobsahuje UA');
      } catch {
        setRegError('Balíček jssip není v buildu frontendu (spusť npm install a přebuild).');
        setRegState('failed');
        return;
      }
      try {
        if (cancelled) return;
        const socket = new JsSIP.WebSocketInterface(meta.wssUrl);
        ua = new JsSIP.UA({
          sockets: [socket],
          uri: `sip:${activeLine.line}@${meta.domain}`,
          password: activeLine.password,
          display_name: activeLine.callerId || activeLine.line,
          register: true,
          session_timers: false
        });
        uaRef.current = ua;
        setRegState('connecting');

        ua.on('registered', () => { if (!cancelled) { setRegState('registered'); setRegError(''); } });
        ua.on('unregistered', () => { if (!cancelled) setRegState('idle'); });
        ua.on('registrationFailed', (e) => {
          if (cancelled) return;
          setRegState('failed');
          setRegError('Registrace na Odorik selhala: ' + (e?.cause || 'zkontroluj číslo linky a SIP heslo'));
        });
        ua.on('newRTCSession', ({ originator, session, request }) => {
          // Ignoruj další příchozí, když už hovor běží
          if (originator === 'remote' && sessionRef.current) { try { session.terminate(); } catch { /* */ } return; }
          wireSession(session);
          if (originator === 'remote') {
            const from = (request && request.from && request.from.uri && request.from.uri.user) || 'Neznámé číslo';
            setCallState('incoming');
            setCallInfo({ number: from });
            recMetaRef.current = { number: from, direction: 'inbound' };
            // Dohledej firmu podle čísla volajícího → nabídne „Otevřít firmu"
            (async () => {
              try {
                const r = await fetch(`${API_URL}/twilio/company-by-phone?userId=${userId}&phone=${encodeURIComponent(from)}`);
                if (r.ok) {
                  const d = await r.json();
                  if (d.found) setCallInfo(ci => ({ ...(ci || {}), companyId: d.companyId, companyName: d.companyName }));
                }
              } catch { /* ignore */ }
            })();
          }
        });

        ua.start();
      } catch (e) {
        if (!cancelled) { setRegError('Inicializace SIP selhala: ' + (e?.message || e)); setRegState('failed'); }
      }
    })();

    return () => {
      cancelled = true;
      try { ua && ua.stop(); } catch { /* */ }
      uaRef.current = null;
      setRegState('idle');
    };
  }, [active, userId, lines, activeLineIdx, wireSession]);

  // Přepnutí aktivní linky (jen když neprobíhá hovor)
  const switchLine = (idx) => {
    if (callState !== 'idle') return;
    localStorage.setItem('odorikActiveLine', lines[idx]?.line || '');
    setActiveLineIdx(idx);
  };

  // ---- Timer hovoru ----
  useEffect(() => {
    if (callState === 'active') {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [callState]);

  // ---- Odchozí hovor ----
  const startCall = useCallback((number, meta = {}) => {
    const ua = uaRef.current;
    const creds = credsRef.current;
    if (!ua || !creds || !number) return;
    if (sessionRef.current) return; // už probíhá hovor
    const target = `sip:${normalize(number)}@${creds.domain}`;
    setCallInfo({ number: normalize(number), companyId: meta.companyId, companyName: meta.companyName, contactName: meta.contactName });
    recMetaRef.current = { number: normalize(number), companyId: meta.companyId, companyName: meta.companyName, direction: 'outbound' };
    setCallState('connecting');
    try {
      // wireSession se navěsí v ua.on('newRTCSession'), který proběhne uvnitř ua.call
      ua.call(target, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] },
        rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 }
      });
    } catch (e) {
      setRegError('Hovor se nepodařilo navázat: ' + (e?.message || e));
      cleanupCall();
    }
  }, [cleanupCall]);

  const acceptIncoming = () => {
    const s = sessionRef.current;
    if (!s) return;
    try {
      s.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }
      });
      setCallState('active');
    } catch { /* */ }
  };
  const rejectIncoming = () => {
    try {
      const s = sessionRef.current;
      // 603 Decline = jednoznačné odmítnutí, aby ústředna hovor už neposílala znovu
      if (s) s.terminate({ status_code: 603, reason_phrase: 'Decline' });
    } catch { /* */ }
    cleanupCall();
  };
  const hangup = () => { try { sessionRef.current && sessionRef.current.terminate(); } catch { /* */ } cleanupCall(); };
  const toggleMute = () => {
    const s = sessionRef.current;
    if (!s) return;
    const m = !muted;
    try { m ? s.mute({ audio: true }) : s.unmute({ audio: true }); setMuted(m); } catch { /* */ }
  };
  const openCompany = () => { if (callInfo?.companyId) { navigate(`/companies/${callInfo.companyId}`); } };

  // ---- Globální event z karty zákazníka ----
  useEffect(() => {
    const onCall = (e) => {
      const { number, companyId, companyName, contactName } = e.detail || {};
      if (number) startCall(number, { companyId, companyName, contactName });
    };
    window.addEventListener('twilio:call', onCall);
    return () => window.removeEventListener('twilio:call', onCall);
  }, [startCall]);

  // Vyžádej si oprávnění na desktop notifikace (jen jednou, po povolení VoIP panelu)
  useEffect(() => {
    if (active && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* ignore */ });
    }
  }, [active]);

  // Desktop notifikace na nový zmeškaný hovor (jen na hovory z posledních 2 minut, aby nespamovaly staré)
  const notifiedCallsRef = useRef(new Set());
  useEffect(() => {
    if (!userId) return;
    missedCalls.forEach(c => {
      const startedAt = new Date(c.startedAt).getTime();
      if (startedAt <= missedSeenAt || notifiedCallsRef.current.has(c._id)) return;
      notifiedCallsRef.current.add(c._id);
      if (Date.now() - startedAt > 2 * 60 * 1000) return;
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('☎️ Zmeškaný hovor', { body: c.fromNumber + (c.companyName ? ` · ${c.companyName}` : '') });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentCalls, userId, missedSeenAt]);

  // Odsazení stránky, aby lišta nepřekrývala obsah
  useEffect(() => {
    if (active) {
      const prev = document.body.style.paddingBottom;
      document.body.style.paddingBottom = '3.5rem';
      return () => { document.body.style.paddingBottom = prev; };
    }
  }, [active]);

  if (!active) return null;

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const btn = 'rounded-lg font-semibold transition disabled:opacity-40';
  const dot = regState === 'registered' ? 'bg-green-500' : (regState === 'failed' ? 'bg-red-500' : 'bg-yellow-400');

  const panel = (
    <>
      <audio ref={audioRef} autoPlay />

      {/* Rozbalený panel s protokolem hovorů */}
      {expanded && (
        <div className={`fixed bottom-[3.25rem] right-3 z-[9997] w-80 max-w-[92vw] rounded-t-xl shadow-2xl border ${isDarkMode ? 'bg-slate-900 border-indigo-500 text-slate-100' : 'bg-white border-indigo-300 text-gray-900'}`}>
          <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className="text-sm font-semibold">☎️ Telefon</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowLog(s => !s)} title="Historie" className="text-xs px-2 py-1 rounded bg-gray-500/20 hover:bg-gray-500/40">🕘</button>
              <button onClick={() => setExpanded(false)} className="text-lg leading-none">▾</button>
            </div>
          </div>

          <div className="p-3 space-y-3">
            {showLog && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  <button onClick={() => setLogFilter('all')} className={`flex-1 text-xs px-2 py-1 rounded ${logFilter === 'all' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200')}`}>Vše</button>
                  <button onClick={() => { setLogFilter('missed'); markMissedSeen(); }} className={`flex-1 text-xs px-2 py-1 rounded ${logFilter === 'missed' ? 'bg-red-600 text-white' : (isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200')}`}>📵 Zmeškané{missedCalls.length ? ` (${missedCalls.length})` : ''}</button>
                </div>
                <div className="max-h-52 overflow-y-auto space-y-1">
                  {(logFilter === 'missed' ? missedCalls : recentCalls).length === 0 ? (
                    <div className="text-xs opacity-60 text-center py-4">{logFilter === 'missed' ? 'Žádné zmeškané hovory' : 'Žádné hovory'}</div>
                  ) : (logFilter === 'missed' ? missedCalls : recentCalls).map(c => (
                    <button key={c._id} onClick={() => startCall(c.direction === 'inbound' ? c.fromNumber : c.toNumber, { companyId: c.companyId, companyName: c.companyName })}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center justify-between ${isMissedCall(c) ? (isDarkMode ? 'bg-red-900/30 hover:bg-red-900/50' : 'bg-red-50 hover:bg-red-100') : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100')}`}>
                      <span>
                        <span className="mr-1">{isMissedCall(c) ? '📵' : (c.direction === 'inbound' ? '📥' : '📤')}</span>
                        <span className={`font-mono ${isMissedCall(c) ? 'text-red-500 font-semibold' : ''}`}>{c.direction === 'inbound' ? c.fromNumber : c.toNumber}</span>
                        {c.companyName && <span className="block opacity-60">{c.companyName}</span>}
                      </span>
                      <span className="opacity-60">{c.startedAt ? new Date(c.startedAt).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Spodní lišta – rozsvítí se zeleně, když probíhá nebo se vytáčí hovor */}
      <div className={`fixed bottom-0 left-0 right-0 z-[9997] border-t-2 transition-colors ${(callState === 'active' || callState === 'connecting') ? 'bg-green-600 border-green-400 text-white' : (isDarkMode ? 'bg-slate-900 border-indigo-500 text-slate-100' : 'bg-slate-700 border-indigo-400 text-slate-50')}`} style={{ height: '3.25rem' }}>
        <div className="max-w-7xl mx-auto h-full px-3 flex items-center gap-3">
          <button onClick={() => setExpanded(x => !x)} className="relative flex items-center gap-2 text-sm font-semibold">
            <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
            📞 VoIP{activeNumber ? <span className="font-mono font-normal opacity-90"> · {activeNumber}</span> : ''}
            {unseenMissedCount > 0 && (
              <span className="ml-1 bg-red-600 text-white text-[10px] rounded-full px-1.5 min-w-[16px] leading-[16px] text-center animate-pulse">{unseenMissedCount}</span>
            )}
          </button>
          {lines.length > 1 && callState === 'idle' && (
            <select
              value={activeLineIdx}
              onChange={e => switchLine(Number(e.target.value))}
              title="Přepnout linku / číslo, ze kterého voláš"
              className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              {lines.map((l, i) => <option key={l.line} value={i}>{l.label ? `${l.label} · ` : ''}{l.callerId || l.line}</option>)}
            </select>
          )}
          {callState === 'active' && <span className="text-sm font-mono">🟢 {callInfo?.number} · {fmtTime(elapsed)}</span>}
          {callState === 'incoming' && <span className="text-sm animate-pulse">📥 Příchozí: {callInfo?.number}</span>}
          {callState === 'connecting' && <span className="text-sm">Vytáčím {callInfo?.number}…</span>}
          {callState !== 'idle' && callInfo?.companyId && (
            <button onClick={openCompany} className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white max-w-[180px] truncate">🏢 {callInfo.companyName || 'Otevřít firmu'}</button>
          )}
          <div className="flex-1" />
          {callState === 'idle' ? (
            <>
              <input value={dialNumber} onChange={e => setDialNumber(e.target.value)} onKeyDown={e => e.key === 'Enter' && startCall(dialNumber)}
                placeholder="Rychlé vytáčení…" className={`w-40 px-2 py-1 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
              <button onClick={() => startCall(dialNumber)} disabled={!dialNumber || regState !== 'registered'} className={`${btn} bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm`}>📞</button>
              <button onClick={() => { setExpanded(true); setShowLog(true); setLogFilter('all'); }} title="Protokol hovorů"
                className={`relative inline-flex items-center gap-1 ${btn} px-3 py-1 text-xs ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}>
                <span>📜</span>
                <span>Protokol</span>
                {unseenMissedCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] rounded-full px-1 min-w-[15px] leading-[15px] text-center">{unseenMissedCount}</span>
                )}
              </button>
            </>
          ) : callState === 'incoming' ? (
            <div className="flex gap-2">
              <button onClick={acceptIncoming} className={`${btn} bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm`}>✅ Přijmout</button>
              <button onClick={rejectIncoming} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm`}>⛔</button>
            </div>
          ) : (
            <div className="flex gap-2">
              {callState === 'active' && (
                <button onClick={toggleMute} className={`${btn} px-3 py-1 text-sm ${muted ? 'bg-yellow-500 text-white' : (isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300')}`}>{muted ? '🔇' : '🎙️'}</button>
              )}
              <button onClick={hangup} className={`${btn} bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm`}>📵 Zavěsit</button>
            </div>
          )}
          {regState === 'failed' && <span className="text-xs text-red-400 max-w-[200px] truncate" title={regError}>{regError}</span>}
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(panel, document.body);
}
